import { useTranslation } from 'react-i18next';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography } from '@mui/material';
import { DialogCloseButton } from 'src/shared/components/organisms/dialogCloseButton';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { useState } from 'react';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { redeemEventParticipantTicket } from 'src/shared/services/eventParticipantTicketService';
import { toast } from 'react-toastify';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { EventParticipantTicket } from '../models/eventParticipantTicket';

type EventTicketDetailModalProps = {
  modalOpen: boolean;
  ticket: EventParticipantTicket;
  eventName?: string;
  eventDate?: string;
  eventLocation?: string;
  onClose: () => void;
  refetchData?: () => void;
  onOpenParticipant?: (participantId: number) => void;
};

const DetailRow = ({ label, value }: { label: string; value: string | number | null | undefined }) => (
  <Box sx={{ mb: 0.8 }}>
    <Typography variant='caption' color='text.disabled' sx={{ display: 'block', lineHeight: 1.2, letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant='body2' fontWeight={500} sx={{ fontFamily: 'inherit' }}>
      {value || '—'}
    </Typography>
  </Box>
);

export const EventTicketDetailModal = (props: EventTicketDetailModalProps) => {
  const { modalOpen, ticket, eventName, eventDate, eventLocation, onClose, refetchData, onOpenParticipant } = props;
  const { t } = useTranslation(['event', 'common']);
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const componentId = `dialog-ticket-${Date.now().toString(36)}`;

  const redeemTicket = async () => {
    setLoading(true);
    try {
      await redeemEventParticipantTicket(ticket.id);
      if (isMounted()) {
        toast.success(t('event:ticketRedeemedSuccess') as string);
        if (refetchData) refetchData();
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        setShowConfirmDialog(false);
        setLoading(false);
      }
    }
  };

  const participantName = ticket.participant ? `${ticket.participant.firstName} ${ticket.participant.lastName}` : null;

  return (
    <Dialog onClose={onClose} aria-labelledby={`${componentId}-titel`} open={modalOpen} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, lineHeight: 1.2 }} id={`${componentId}-titel`}>
        <Box sx={{ fontWeight: 600 }}>
          {t('event:ticketModal.title')} #{ticket.eventTicketNr}
        </Box>
        <DialogCloseButton handleClose={onClose} />
      </DialogTitle>

      <DialogContent dividers>
        {/* Biljettkort — biobiljett-stil */}
        <Box
          sx={{
            display: 'flex',
            border: '1px solid #e0e0e0',
            borderRadius: '12px',
            overflow: 'hidden',
            mb: 2,
            position: 'relative',
            boxShadow: '2px 3px 0px rgba(0,0,0,0.06), 1px 1px 0px rgba(0,0,0,0.04)',
            backgroundColor: '#fdfdfd',
          }}
        >
          {/* Vattenstämpel */}
          {!ticket.isRedeemed && (
            <Typography
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(-25deg)',
                fontSize: '2.5rem',
                fontWeight: 700,
                fontFamily: 'monospace',
                color: 'rgba(211, 47, 47, 0.12)',
                textTransform: 'uppercase',
                letterSpacing: 4,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
                zIndex: 1,
              }}
            >
              Ej inlöst
            </Typography>
          )}
          {ticket.isRedeemed && (
            <Typography
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(-25deg)',
                fontSize: '2.5rem',
                fontWeight: 700,
                fontFamily: 'monospace',
                color: 'rgba(46, 125, 50, 0.12)',
                textTransform: 'uppercase',
                letterSpacing: 4,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
                zIndex: 1,
              }}
            >
              {t('event:ticketModal.redeemed')}
            </Typography>
          )}
          {/* Vänster — biljettinfo */}
          <Box
            sx={{
              flex: 1,
              p: 2.5,
              fontFamily: 'monospace',
              borderRight: '2px dashed #e0e0e0',
              position: 'relative',
              '&::before, &::after': {
                content: '""',
                position: 'absolute',
                right: -9,
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: (thm) => thm.palette.background.default,
                border: '1px solid #e0e0e0',
              },
              '&::before': { top: -8 },
              '&::after': { bottom: -8 },
            }}
          >
            <Typography variant='caption' sx={{ letterSpacing: 2, textTransform: 'uppercase', color: 'text.disabled' }}>
              {t('event:ticketModal.ticketLabel')}
            </Typography>
            <Typography variant='subtitle1' sx={{ mb: 1.5 }}>
              {ticket.ticket?.ticketName}
            </Typography>

            {participantName && <DetailRow label='Namn' value={participantName} />}
            {eventLocation && <DetailRow label='Plats' value={eventLocation} />}
            {eventDate && <DetailRow label='Datum' value={DateHelper.getDateTime(eventDate)} />}
          </Box>

          {/* Höger — biljettnummer */}
          <Box
            sx={{
              width: 100,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f9f9f9',
              p: 1.5,
            }}
          >
            <Typography variant='caption' color='text.disabled' sx={{ mb: 0.5 }}>
              {t('event:participantDetails.ticketNrLabelFull')}
            </Typography>
            <Typography variant='h5' fontWeight={700} sx={{ fontFamily: 'monospace' }}>
              #{ticket.eventTicketNr}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 0.8 }}>
          <Typography variant='caption' color='text.disabled'>
            {t('event:ticketModal.bookingCreated')} {DateHelper.getDateTime(ticket.createdAt)}
            {ticket.participant?.bookedByName && ticket.participant.bookedByParticipantId && (
              <>
                {' av '}
                {onOpenParticipant ? (
                  <Typography
                    component='span'
                    variant='caption'
                    color='primary'
                    sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => {
                      onClose();
                      onOpenParticipant(ticket.participant!.bookedByParticipantId!);
                    }}
                  >
                    {ticket.participant.bookedByName}
                  </Typography>
                ) : (
                  ticket.participant.bookedByName
                )}
              </>
            )}
          </Typography>
        </Box>
        {ticket.isRedeemed && ticket.redeemedAt && (
          <Typography variant='caption' color='text.disabled'>
            {t('event:ticketModal.redeemed')} {DateHelper.getDateTime(ticket.redeemedAt)}
            {ticket.redeemedByName ? ` av ${ticket.redeemedByName}` : ''}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        {!ticket.isRedeemed && (
          <Button variant='contained' color='success' disableElevation disabled={loading} onClick={() => setShowConfirmDialog(true)}>
            {t('event:redeemTicket')}
          </Button>
        )}
        <Button variant='outlined' disableElevation onClick={onClose}>
          {t('common:button.close')}
        </Button>
      </DialogActions>

      {showConfirmDialog && (
        <DeleteConfirmationDialog
          open={showConfirmDialog}
          confirmButtonText={t('event:redeemTicket')}
          confirmButtonColor='success'
          title={t('event:redeemTicket')}
          value=''
          modaltext={t('event:confirmRedeemTicket')}
          onClose={(value) => {
            if (value) {
              redeemTicket();
            } else {
              setShowConfirmDialog(false);
            }
          }}
        />
      )}
    </Dialog>
  );
};
